import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { TrendingUp, Users2 } from "lucide-react";

export default function Analytics() {
  const { t } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/analytics/summary")
      .then(({ data }) => { if (!cancelled) setD(data); })
      .catch((error) => { console.error("Failed to load analytics:", error); });
    return () => { cancelled = true; };
  }, []);
  const revenue14 = d?.revenue_14d || [];
  const max = Math.max(1, ...revenue14.map(x => x.revenue));

  return (
    <DashboardLayout title={t("analytics.title")} subtitle={t("analytics.subtitle")}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="analytics-grid">
        <div className="lg:col-span-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="analytics-revenue">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-sm font-semibold text-zinc-100">Revenue (14d)</h3>
            <TrendingUp className="w-4 h-4 text-blue-400" />
          </div>
          <div className="h-56 flex items-end gap-1">
            {revenue14.map((b) => (
              <div key={b.day} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-sm bg-gradient-to-t from-blue-500/70 to-cyan-400/70" style={{ height: `${(b.revenue / max) * 100}%` }} title={`$${b.revenue}`} />
                <div className="text-[9px] text-zinc-500">{b.day.slice(5)}</div>
              </div>
            ))}
            {revenue14.length === 0 && <div className="w-full text-center text-xs text-zinc-500">No data</div>}
          </div>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="analytics-projects">
          <h3 className="font-display text-sm font-semibold text-zinc-100 mb-4">Projects by status</h3>
          <ul className="space-y-2 text-sm">
            {(d?.projects_by_status || []).map((r) => (
              <li key={r._id} className="flex items-center justify-between">
                <span className="text-zinc-300">{r._id || "unknown"}</span>
                <span className="text-blue-400">{r.count}</span>
              </li>
            ))}
            {(!d?.projects_by_status || d.projects_by_status.length === 0) && <li className="text-xs text-zinc-500">No data</li>}
          </ul>
        </div>

        <div className="lg:col-span-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="analytics-leads">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-sm font-semibold text-zinc-100">Leads by stage</h3>
            <Users2 className="w-4 h-4 text-blue-400" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {(d?.leads_by_stage || []).map((r) => (
              <div key={r._id} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">{r._id || "unknown"}</div>
                <div className="text-2xl text-zinc-100 mt-1 font-display">{r.count}</div>
              </div>
            ))}
            {(!d?.leads_by_stage || d.leads_by_stage.length === 0) && <div className="text-xs text-zinc-500 col-span-full">No data</div>}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
